// src/pages/Admin/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaListAlt, FaUserPlus, FaEye, FaChartLine, FaUsers } from 'react-icons/fa';
import TitleWithBack from '../../components/common/TitleWithBack';
import WorkStatsPanel from '../../components/reports/WorkStatsPanel';
function Dashboard() {
  const [totalSeries, setTotalSeries] = useState(0);
  const [registeredUsers, setRegisteredUsers] = useState(0);
  const [upcomingExams, setUpcomingExams] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch exam series
        const seriesRes = await axios.get('/api/exam-series');
        const seriesData = Array.isArray(seriesRes.data)
          ? seriesRes.data
          : seriesRes.data.content || [];
        setTotalSeries(seriesData.length);

        // Fetch users
        const usersRes = await axios.get('/api/users');
        const usersData = Array.isArray(usersRes.data)
          ? usersRes.data
          : usersRes.data.content || [];
        setRegisteredUsers(usersData.length);

        // Fetch exams and count those in an active series whose scheduled
        // date/time have not yet passed.
        const examsRes = await axios.get('/api/exams');
        const examsData = Array.isArray(examsRes.data)
          ? examsRes.data
          : examsRes.data.content || [];
        const now = new Date();
        const upcoming = examsData.filter((exam) => {
          if (!exam.examDate) return false;
          const series = exam.examSeries;
          if (!series || !series.startDate || !series.endDate) return false;
          const seriesStart = new Date(series.startDate);
          const seriesEnd = new Date(series.endDate);
          seriesEnd.setHours(23, 59, 59, 999);
          if (now < seriesStart || now > seriesEnd) return false;

          const dueDateTime = new Date(exam.examDate);
          if (exam.startTime) {
            const [hours, minutes] = exam.startTime.split(':').map(Number);
            dueDateTime.setHours(hours, minutes, 0, 0);
          } else {
            dueDateTime.setHours(23, 59, 59, 999);
          }
          return dueDateTime >= now;
        });
        setUpcomingExams(upcoming.length);

        setLoading(false);
      } catch (err) {
        console.error(err);
        setError('Error fetching dashboard data.');
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  return (
    <div className="container-fluid">
      {/* Header / Title */}
      <div className="row mt-4 mb-3">
        <div className="col">
          {/* <TitleWithBack title="Dashboard" level="h2" /> */}
          <p className="lead text-secondary">
            
          </p>
        </div>
      </div>

      {/* Display loader or error if needed */}
      {loading ? (
        <p>Loading dashboard data...</p>
      ) : error ? (
        <p className="text-danger">{error}</p>
      ) : (
        <>
          {/* Statistics Cards */}
          <div className="row row-cols-1 row-cols-md-3 g-3 mb-4">
            <div className="col">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center">
                  <FaChartLine size={32} className="text-primary mb-2" />
                  <h5 className="card-title fw-bold mb-2">Total Series</h5>
                  <p className="display-6 text-primary">{totalSeries}</p>
                  <p className="small text-muted">Active exam series</p>
                </div>
              </div>
            </div>
            <div className="col">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center">
                  <FaUsers size={32} className="text-success mb-2" />
                  <h5 className="card-title fw-bold mb-2">Registered Users</h5>
                  <p className="display-6 text-success">{registeredUsers}</p>
                  <p className="small text-muted">All system users</p>
                </div>
              </div>
            </div>
            <div className="col">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center">
                  <FaListAlt size={32} className="text-danger mb-2" />
                  <h5 className="card-title fw-bold mb-2">Upcoming Exams</h5>
                  <p className="display-6 text-danger">{upcomingExams}</p>
                  <p className="small text-muted">Not yet due, in active series</p>
                </div>
              </div>
            </div>
          </div>

          {/* Work Upload Progress */}
          <div className="row mb-4">
            <div className="col">
              <h2 className="h4 fw-bold mb-3">Work Upload Progress</h2>
              <WorkStatsPanel />
            </div>
          </div>

          {/* Quick Actions */}
          <div className="row mb-4">
            <div className="col">
              <h2 className="h4 fw-bold mb-3">Quick Actions</h2>
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-3">
                {/* Define New Series */}
                <div className="col">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-body d-flex flex-column">
                      <div className="mb-3 d-flex align-items-center">
                        <FaListAlt className="text-primary me-2" size={24} />
                        <span className="fw-bold">Define New Series</span>
                      </div>
                      <p className="text-muted small flex-grow-1">
                        Create a new exam series, set start/end dates, and assign programs.
                      </p>
                      <Link to="/admin/series/define" className="btn btn-primary btn-sm mt-auto">
                        Go
                      </Link>
                    </div>
                  </div>
                </div>
                {/* View All Series */}
                <div className="col">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-body d-flex flex-column">
                      <div className="mb-3 d-flex align-items-center">
                        <FaEye className="text-primary me-2" size={24} />
                        <span className="fw-bold">View All Series</span>
                      </div>
                      <p className="text-muted small flex-grow-1">
                        Manage and review all exam series.
                      </p>
                      <Link to="/admin/series/view" className="btn btn-primary btn-sm mt-auto">
                        Go
                      </Link>
                    </div>
                  </div>
                </div>
                {/* Create New User */}
                <div className="col">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-body d-flex flex-column">
                      <div className="mb-3 d-flex align-items-center">
                        <FaUserPlus className="text-primary me-2" size={24} />
                        <span className="fw-bold">Create New User</span>
                      </div>
                      <p className="text-muted small flex-grow-1">
                        Register a new user with the appropriate role.
                      </p>
                      <Link to="/admin/users/create" className="btn btn-primary btn-sm mt-auto">
                        Go
                      </Link>
                    </div>
                  </div>
                </div>
                {/* View All Users */}
                <div className="col">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-body d-flex flex-column">
                      <div className="mb-3 d-flex align-items-center">
                        <FaEye className="text-primary me-2" size={24} />
                        <span className="fw-bold">View All Users</span>
                      </div>
                      <p className="text-muted small flex-grow-1">
                        Manage user accounts and roles.
                      </p>
                      <Link to="/admin/users/view" className="btn btn-primary btn-sm mt-auto">
                        Go
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;
